// djin-cloud/web/src/components/site-detail/tabs/StatisticsTab.tsx

import { useState } from 'react';
import { AreaProfileChart } from '../../charts/echarts/AreaProfileChart';
import { useEnergyProfile } from '../../../hooks/useEnergyProfile';

// ── Internal sub-components ────────────────────────────────────────────────────

interface KPICardProps {
  label: string;
  value: string | number;
  unit: string;
  color?: string;
}

function KPICard({ label, value, unit, color }: KPICardProps) {
  return (
    <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-4">
      <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">
        {label}
      </div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className={`text-xl font-bold font-mono ${color ?? 'text-ems-text-primary'}`}>
          {value}
        </span>
        <span className="text-xs text-ems-text-muted">{unit}</span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-ems-bg-card border border-ems-border p-4 h-20" />
        ))}
      </div>
      <div className="rounded-2xl bg-ems-bg-card border border-ems-border h-96" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-ems-bg-card border border-ems-border p-4 h-24" />
        ))}
      </div>
    </div>
  );
}

// ── Time range constants ───────────────────────────────────────────────────────

const TIME_RANGES = [
  { key: '24h', label: '24H' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: '1y', label: '1Y' },
] as const;

type TimeRangeKey = (typeof TIME_RANGES)[number]['key'] | 'custom';

// ── Main component ─────────────────────────────────────────────────────────────

interface StatisticsTabProps {
  siteId: string;
}

export function StatisticsTab({ siteId }: StatisticsTabProps) {
  const [range, setRange] = useState<TimeRangeKey>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { data, isLoading, isError } = useEnergyProfile(
    siteId,
    range,
    range === 'custom' ? customStart : undefined,
    range === 'custom' ? customEnd : undefined,
  );

  const profile: { timestamp: string; consumption: number; generation: number }[] =
    (data?.data ?? []).map((d: { timestamp: string; consumption_kw: number; generation_kw: number }) => ({
      timestamp: d.timestamp,
      consumption: d.consumption_kw,
      generation: d.generation_kw,
    }));
  const summary = data?.summary;

  // Derived delta metrics
  const totalConsumption: number = summary?.total_consumption_kwh ?? 0;
  const totalGeneration: number = summary?.total_generation_kwh ?? 0;
  const netEnergyBalanceMWh = (totalGeneration - totalConsumption) / 1000;
  const selfSufficiencyPct =
    totalConsumption > 0
      ? Math.min(100, (totalGeneration / totalConsumption) * 100)
      : 0;

  // Days in selected range for avg daily delta
  const customDays =
    range === 'custom' && customStart && customEnd
      ? Math.max(1, Math.round((new Date(customEnd).getTime() - new Date(customStart).getTime()) / 86_400_000))
      : 1;
  const rangeDays: Record<TimeRangeKey, number> = {
    '24h': 1,
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365,
    custom: customDays,
  };
  const days = rangeDays[range];
  const avgDailyDeltaKwh = (totalGeneration - totalConsumption) / days;

  const netBalanceColor =
    netEnergyBalanceMWh >= 0 ? 'text-ems-green' : 'text-ems-red';

  return (
    <div className="p-6 bg-ems-bg min-h-full space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-base font-semibold text-ems-text-primary">Historical Analytics</h2>

        {/* Time range controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-xl bg-ems-bg-card border border-ems-border p-1">
            {TIME_RANGES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setRange(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  range === key
                    ? 'bg-ems-green/20 text-ems-green'
                    : 'text-ems-text-muted hover:text-ems-text-secondary'
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setRange('custom')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                range === 'custom'
                  ? 'bg-ems-green/20 text-ems-green'
                  : 'text-ems-text-muted hover:text-ems-text-secondary'
              }`}
            >
              Custom
            </button>
          </div>

          {range === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded-lg bg-ems-bg-card border border-ems-border px-3 py-1.5 text-sm text-ems-text-primary font-mono focus:outline-none focus:ring-1 focus:ring-ems-green/40"
              />
              <span className="text-ems-text-muted text-sm">—</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded-lg bg-ems-bg-card border border-ems-border px-3 py-1.5 text-sm text-ems-text-primary font-mono focus:outline-none focus:ring-1 focus:ring-ems-green/40"
              />
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : isError ? (
        <div className="flex items-center justify-center h-64 text-ems-text-muted text-sm">
          Failed to load energy profile data.
        </div>
      ) : (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard
              label="Total Consumption"
              value={summary?.total_consumption_kwh?.toFixed(1) ?? '—'}
              unit="kWh"
            />
            <KPICard
              label="Total Generation"
              value={summary?.total_generation_kwh?.toFixed(1) ?? '—'}
              unit="kWh"
              color="text-ems-green"
            />
            <KPICard
              label="Peak Demand"
              value={summary?.peak_demand_kw?.toFixed(1) ?? '—'}
              unit="kW"
            />
            <KPICard
              label="Avg Load"
              value={summary?.avg_load_kw?.toFixed(1) ?? '—'}
              unit="kW"
            />
            <KPICard
              label="Renewable Mix"
              value={summary?.renewable_mix_pct?.toFixed(1) ?? '—'}
              unit="%"
              color="text-ems-green"
            />
          </div>

          {/* ── Area Profile Chart ── */}
          <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-6">
            {profile.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-ems-text-muted text-sm">
                No profile data available for the selected period.
              </div>
            ) : (
              <AreaProfileChart data={profile} height={360} />
            )}
          </div>

          {/* ── Delta Summary Row ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Net Energy Balance */}
            <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-4">
              <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">
                Net Energy Balance
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-xl font-bold font-mono ${netBalanceColor}`}>
                  {netEnergyBalanceMWh >= 0 ? '+' : ''}
                  {netEnergyBalanceMWh.toFixed(3)}
                </span>
                <span className="text-xs text-ems-text-muted">MWh</span>
              </div>
              <p className="mt-1 text-xs text-ems-text-muted">
                {netEnergyBalanceMWh >= 0 ? 'Generation surplus' : 'Consumption deficit'}
              </p>
            </div>

            {/* Self-Sufficiency */}
            <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-4">
              <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">
                Self-Sufficiency
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span
                  className={`text-xl font-bold font-mono ${
                    selfSufficiencyPct >= 80
                      ? 'text-ems-green'
                      : selfSufficiencyPct >= 40
                        ? 'text-ems-amber'
                        : 'text-ems-red'
                  }`}
                >
                  {selfSufficiencyPct.toFixed(1)}
                </span>
                <span className="text-xs text-ems-text-muted">%</span>
              </div>
              <p className="mt-1 text-xs text-ems-text-muted">
                Generation / consumption ratio
              </p>
            </div>

            {/* Avg Daily Delta */}
            <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-4">
              <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">
                Avg Daily Delta
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span
                  className={`text-xl font-bold font-mono ${
                    avgDailyDeltaKwh >= 0 ? 'text-ems-green' : 'text-ems-red'
                  }`}
                >
                  {avgDailyDeltaKwh >= 0 ? '+' : ''}
                  {avgDailyDeltaKwh.toFixed(1)}
                </span>
                <span className="text-xs text-ems-text-muted">kWh/day</span>
              </div>
              <p className="mt-1 text-xs text-ems-text-muted">
                Over {days} day{days !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
